/*
  TYOHAAR Instagram — latest posts, automatically.

  sections/instagram-tyohaar.liquid with Posts = "Latest from Instagram".

  The posts come from Instafeed, the app connected to @shoptyohaar. Its own
  widget is not used: it brings its own layout, spacing, footer link and a
  popup. This reads the same feed the widget reads — Instafeed's public feed
  endpoint, which answers this shop's own domains — and draws each post in
  our own tile, so the row looks exactly like the hand-picked one and every
  tile opens the post on Instagram.

  Instafeed keeps the feed in step with Instagram (it syncs hourly), so a new
  post appears here without anyone touching the theme.

  The last result is kept in localStorage and drawn first, so a returning
  visitor sees the row at once; the live feed then replaces it if it changed.
  Instagram's picture links expire after a while, which is why the live feed
  is always fetched rather than trusting the copy.
*/

(() => {
  if (customElements.get('tyoh-ig-auto')) return;

  const ENDPOINT = 'https://api.nfcube.com/feed/v6';

  const recall = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };

  const store = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* private mode: fetched every time */
    }
  };

  const REEL_MARK =
    '<svg viewBox="0 0 24 24" focusable="false">' +
    '<rect x="2.75" y="2.75" width="18.5" height="18.5" rx="5" fill="none" stroke="currentColor" stroke-width="2"/>' +
    '<path d="M3 8.25h18M8.6 3l2.6 5.25M14.4 3l2.6 5.25" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
    '<path d="M10 11.6v5.3a.45.45 0 0 0 .69.38l4.1-2.65a.45.45 0 0 0 0-.76l-4.1-2.65a.45.45 0 0 0-.69.38Z" fill="currentColor"/>' +
    '</svg>';

  // A Reel is a Reel by its link: Instagram reports some Reels as "image"
  // (the ones whose audio cannot be shown off Instagram).
  const isReel = (post) => post.type === 'video' || /\/reels?\//.test(post.link || '');

  const toPosts = (json) => {
    try {
      const data = JSON.parse(json);
      return Array.isArray(data.data) ? data.data : [];
    } catch (e) {
      return [];
    }
  };

  class TyohIgAuto extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.rail = document.getElementById(this.dataset.rail);
      if (!this.rail) return;

      this.key = `tyoh-ig-auto:${this.dataset.account}`;
      const cached = recall(this.key);
      if (cached) this.draw(toPosts(cached));
      this.refresh(cached);
    }

    get count() {
      return parseInt(this.dataset.count, 10) || 10;
    }

    async refresh(cached) {
      // Asked for more than shown, so leaving Reels out still fills the row.
      const limit = Math.min(this.count * 2, 40);
      const url =
        `${ENDPOINT}?account=${encodeURIComponent(this.dataset.account)}` +
        `&fu=0&fid=${encodeURIComponent(this.dataset.feed || '0')}` +
        `&limit=${limit}&client=widget&handle=`;
      try {
        const response = await fetch(url, { credentials: 'omit' });
        if (!response.ok) throw new Error(response.status);
        const json = await response.text();
        if (!toPosts(json).length) throw new Error('empty');
        store(this.key, json);
        if (json !== cached) this.draw(toPosts(json));
      } catch (e) {
        // Nothing to show and nothing saved: hide the empty row rather than
        // leave grey squares on the page.
        if (!cached) this.rail.closest('.ig')?.setAttribute('hidden', '');
      }
    }

    draw(posts) {
      const withReels = this.dataset.reels !== 'false';
      const markReels = this.dataset.reelMark !== 'false';
      const label = this.dataset.label || 'Open on Instagram';

      const items = posts
        .filter((post) => post.link && post.images?.standard_resolution?.url)
        .filter((post) => withReels || !isReel(post))
        .slice(0, this.count)
        .map((post, index) => {
          const li = document.createElement('li');
          li.className = 'ig__item ig__item--in';
          li.style.setProperty('--ig-i', index);

          const a = document.createElement('a');
          a.className = 'ig__tile ig__tile--auto';
          a.href = post.link;
          a.target = '_blank';
          a.rel = 'noopener';
          a.setAttribute('aria-label', label);

          const img = document.createElement('img');
          img.className = 'ig__image';
          img.src = post.images.standard_resolution.url;
          img.alt = (post.caption?.text || '').split('\n')[0].slice(0, 120);
          img.loading = 'lazy';
          img.decoding = 'async';
          // Fades in once loaded (see .is-loaded); a cached picture is already complete.
          const shown = () => img.classList.add('is-loaded');
          img.addEventListener('load', shown, { once: true });
          img.addEventListener('error', shown, { once: true });
          a.append(img);
          if (img.complete) shown();

          if (markReels && isReel(post)) {
            const mark = document.createElement('span');
            mark.className = 'ig__reel-mark';
            mark.setAttribute('aria-hidden', 'true');
            mark.innerHTML = REEL_MARK;
            a.append(mark);
          }

          li.append(a);
          return li;
        });

      if (!items.length) return;
      // The entrance runs once; a redraw from fresh data should not replay it.
      if (this.drawn) items.forEach((li) => li.classList.remove('ig__item--in'));
      this.drawn = true;
      this.rail.replaceChildren(...items);
      this.rail.removeAttribute('aria-busy');
      this.rail.closest('.ig')?.removeAttribute('hidden');
    }
  }

  customElements.define('tyoh-ig-auto', TyohIgAuto);
})();
